import { createApiPath } from '@/shared/api/endpoints';
import { apiClient } from '@/shared/api/http-client';

/**
 * 웹 의약품 API (`/web/drugs/**`, M-10 ~ M-12 · M-15) — 필드 이름은 서버 그대로 둔다 (docs/api/drugs.md).
 * 수수료율은 % 단위(32 = 32%), 금액은 원. null은 "—", 실제 0은 0%/0원으로 구분한다.
 */

/** 정렬: 의약품 이름순 / 수수료율 높은순 */
export const DRUG_SORT_KEYS = [
  'DRUG_NAME_ASC',
  'PHARMA_FEE_RATE_DESC',
] as const;
export type DrugSortKey = (typeof DRUG_SORT_KEYS)[number];

/**
 * 태그 필터 — 앞 4종은 서로 AND, 급여 계열 6종은 서로 OR, 제약사 ID끼리 OR, 그룹 사이는 AND. 결합은 서버가 한다.
 * 화면 순서도 이 배열 순서를 따른다 (M-11 필터 레일).
 */
export const DRUG_FILTERS = [
  'EXCLUDE_OUT_OF_STOCK',
  'REFERENCE_DRUG',
  'BIOEQUIVALENT',
  'EXCLUDE_NEW_DRUG_ITEMS',
  'REIMBURSEMENT',
  'REIMBURSEMENT_SUSPENDED',
  'PROTECTED_REIMBURSEMENT',
  'CALCULATION_NOT_ALLOWED',
  'ARBITRARY_NOT_ALLOWED',
  'NON_REIMBURSEMENT',
] as const;
export type DrugFilter = (typeof DRUG_FILTERS)[number];

export const DRUG_FILTER_LABELS: Record<DrugFilter, string> = {
  EXCLUDE_OUT_OF_STOCK: '품절 제외',
  REFERENCE_DRUG: '대조약',
  BIOEQUIVALENT: '생동인정',
  EXCLUDE_NEW_DRUG_ITEMS: '신규금지 품목 제외',
  REIMBURSEMENT: '급여',
  REIMBURSEMENT_SUSPENDED: '급여 정지',
  PROTECTED_REIMBURSEMENT: '보훈 급여',
  CALCULATION_NOT_ALLOWED: '산정불가',
  ARBITRARY_NOT_ALLOWED: '임의불가',
  NON_REIMBURSEMENT: '비급여',
};

/** 서버 공통 페이지 응답 (offset 기반, page는 0부터) */
export interface PageResponse<T> {
  content: T[];
  pageNo: number;
  pageSize: number;
  isLast: boolean;
  totalCount: number;
}

/** 의약품 카드 — 검색 결과 · 상세 drug · 동일 성분 drug가 같은 모양이다 */
export interface DrugCard {
  /** 원본 코드. ru로 시작하면 표시만 "—" */
  drugCode: string;
  drugName: string;
  /** 의약품 정보 기준월 yyyyMM */
  referenceYearMonth: string;
  pharmaCompanyId: number | null;
  pharmaCompanyName: string | null;
  mainIngredientCode: string | null;
  mainIngredientName: string | null;
  mainIngredientNameEnglish: string | null;
  reimbursementType: string | null;
  referenceDrug: boolean;
  bioequivalent: boolean;
  /** 제약사 약품 코드 존재 여부 — 개인별 거래 승인 상태와 다르다 */
  tradable: boolean;
  /** 제약사 기준 최소 처방 금액 */
  minPrescriptionAmount: number | null;
  price: number | null;
  /** 제약사 수수료율 (%) */
  pharmaFeeRate: number | null;
  /** 사용자 수수료율 (%) — 플랫폼 요율 차감 후 0 하한. 미확정이면 null */
  userFeeRate: number | null;
}

export interface DrugSearchParams {
  keyword: string;
  isDrugTradable?: boolean;
  sortKey?: DrugSortKey;
  filters?: DrugFilter[];
  pharmaCompanyIds?: number[];
  page?: number;
  size?: number;
}

/** GET /suggestions — 두 글자 미만이면 [], 최대 10개 */
export interface DrugSuggestion {
  drugCode: string;
  drugName: string;
  pharmaCompanyName: string | null;
  mainIngredientName: string | null;
}

export interface PharmaCompany {
  pharmaCompanyId: number;
  pharmaCompanyName: string;
}

export interface DrugClinic {
  clinicId: number;
  clinicName: string;
  clinicType: string;
}

/** GET /{drugCode} */
export interface DrugDetail {
  drug: DrugCard;
  unitQuantity: string | null;
  baseUnit: string | null;
  /** 전문/일반 */
  etcOtcType: string | null;
  /** 100회 처방 예상액 — 제약사 · 사용자 기준 (실제 정산 보장액이 아니다) */
  pharmaEstimatedAmount: number | null;
  userEstimatedAmount: number | null;
  /** 로그인 사용자 본인의 기준월 처방 병의원 */
  prescribingClinics: DrugClinic[];
  /** 허가사항 원문 — HTML/XML일 수 있어 허용 목록으로 정제해 렌더링한다 */
  direction: string | null;
  effect: string | null;
  precaution: string | null;
}

/** 동일 성분 비교 기준 */
export type FeeBasis = 'PHARMA' | 'USER';

/**
 * GET /{drugCode}/related 행 — 현재 약이 첫 행(current=true), 이후 예상액↓ → 약품명 → 코드순.
 * feeRate · estimatedAmount · differenceAmount는 모두 요청한 feeBasis 기준이다.
 */
export interface RelatedDrug {
  drug: DrugCard;
  current: boolean;
  /** 주성분 코드 일치가 아니라 제형군 확장으로 들어온 행 */
  sameFormulationGroup: boolean;
  feeRate: number | null;
  estimatedAmount: number | null;
  /** 후보 예상액 − 현재 약 예상액. 한쪽이라도 미확정이면 null */
  differenceAmount: number | null;
}

/** filters · pharmaCompanyIds는 반복 키(filters=A&filters=B)로 보낸다 */
const repeatParams = { indexes: null } as const;

const path = createApiPath.drugs;

export const drugApi = {
  search: async (params: DrugSearchParams) => {
    const { data } = await apiClient.get<PageResponse<DrugCard>>(
      path.search(),
      { params, paramsSerializer: repeatParams },
    );
    return data;
  },
  /** 이전 요청은 signal로 취소해 오래된 응답이 최신 결과를 덮지 않게 한다 */
  suggestions: async (keyword: string, signal?: AbortSignal) => {
    const { data } = await apiClient.get<DrugSuggestion[]>(path.suggestions(), {
      params: { keyword },
      signal,
    });
    return data;
  },
  /**
   * 거래 가능한 약품이 있는 제약사만 온다. 선택한 제약사 · 거래 가능 체크로는 선택지를 좁히지 않는다
   * (검색어 · 약품 필터만 보낸다)
   */
  pharmaCompanies: async (keyword: string, filters?: DrugFilter[]) => {
    const { data } = await apiClient.get<PharmaCompany[]>(
      path.pharmaCompanies(),
      { params: { keyword, filters }, paramsSerializer: repeatParams },
    );
    return data;
  },
  /** 404 DRUG_NOT_FOUND — 없거나 공개 기준월에 비노출인 약품 */
  detail: async (drugCode: string) => {
    const { data } = await apiClient.get<DrugDetail>(path.detail(drugCode));
    return data;
  },
  related: async (
    drugCode: string,
    feeBasis: FeeBasis,
    includeSameFormulationGroup: boolean,
  ) => {
    const { data } = await apiClient.get<RelatedDrug[]>(
      path.related(drugCode),
      { params: { feeBasis, includeSameFormulationGroup } },
    );
    return data;
  },
};
